<template>
  <v-layout>
    <v-flex
      v-if="
        items.length > 0 ||
        !forItinerary ||
        forPiApprovals ||
        (!noFilters && items.length == 0)
      "
      xs12
    >
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="center">
              <div>
                <h1 class="headline" v-if="!forItinerary && !hideTitle">
                  Purchase Invoices
                </h1>
              </div>
              <div>
                <slot name="leftHeader"></slot>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find Invoice"
                  :ref-name="refName"
                  @searchParamsChanged="searchParamsChanged($event)"
                  :cached-filters="cachedFilters"
                  :expansionPanel="showFilters"
                ></list-filters>
              </v-col>

              <div v-if="!hideAgGrid" class="pr-2">
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <v-select
                v-if="showGroupedView"
                :items="groupByOptions"
                v-model="selectedGroupedBy"
                label="Group by"
                item-text="name"
                return-object
                :outlined="$store.getters.localSettings('outlinedInputs')"
                hide-details
              ></v-select>
              <v-switch
                prepend-icon="mdi-view-list"
                class="mr-3"
                label="Group View"
                v-model="showGroupedView"
              ></v-switch>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
              <div>
                <slot></slot>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <v-row v-if="showGroupedView">
        <v-col class="py-0">
          <list-grouped :group="selectedGroupedBy" :items="items">
            <template v-slot:table="{ items }">
              <v-card>
                <pi-table
                  :items="items"
                  :headers="filteredHeaders"
                  :search="search"
                  @delete="deleteItem"
                  @edit="editItem"
                  :selectedItems="selected"
                  @updateSelection="onUpdateSelection"
                ></pi-table>
              </v-card>
            </template>
          </list-grouped>
          <list-actions
            v-if="selected.length > 0"
            style="top: -30px; position: relative"
            :selected="selected"
            list="purchaseInvoices"
            :columns="visibleColumns"
            :filter="searchParams"
            :hideExport="hideExport"
          >
          </list-actions>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <div v-if="showAgGrid" key="ag-grid">
            <ag-grid-vue
              class="ag-theme-alpine"
              id="ag-pi-list"
              :columnDefs="agGridColumns"
              :rowData="items"
              rowSelection="multiple"
              :context="agContext"
              @grid-ready="onAgGridReady"
              @columnMoved="onAgColumnMoved"
              @columnResized="onAgColumnResized"
              @sortChanged="onAgSortChanged"
              @selection-changed="onAgSelectionChanged"
              @newColumnsLoaded="onAgNewColumns"
              suppressDragLeaveHidesColumns
            >
            </ag-grid-vue>
            <list-actions
              v-if="selected.length > 0"
              style="top: -30px; position: relative"
              :selected="selected"
              list="purchaseInvoices"
              :columns="visibleColumns"
              :filter="searchParams"
              :hideExport="hideExport"
            >
            </list-actions>
          </div>
          <v-data-table
            v-else
            :fixed-header="fixedHeader"
            hide-default-footer
            :headers="filteredHeaders"
            :items="items"
            :loading="$apollo.loading"
            v-bind="pagingOptions"
            :search="search"
            item-key="id"
            class="elevation-1"
            :show-select="multiSelect"
            v-model="selected"
            @update:options="onListOptionsUpdate"
            :height="gridHeight"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            @current-items="dataTableReady"
            suppressDragLeaveHidesColumns
          >
            <template v-slot:item.menuActions="{ item }">
              <list-menu-actions
                :item="item"
                @delete="deleteItem"
                @edit="editItem"
                :title-labeler="(item) => item.number"
              ></list-menu-actions>
            </template>
            <template v-slot:item.number="{ item }">
              <v-btn text small @click="showDetails(item)">
                <v-icon left small>mdi-format-list-bulleted-square</v-icon>
                {{ item.number }}
              </v-btn>
            </template>

            <template v-slot:item.itinerary.clientName="{ item }">
              <client-label
                v-if="item.itinerary"
                v-model="item.itinerary.clientName"
                :client-id="
                  item.itinerary && item.itinerary.client
                    ? item.itinerary.client.id
                    : ''
                "
              ></client-label>
            </template>
            <template v-slot:item.itineraryStartDate="{ item }">{{
              item.itineraryStartDate | formatDate
            }}</template>
            <template v-slot:item.firstServiceStartDate="{ item }">{{
              item.firstServiceStartDate | formatDate
            }}</template>
            <template v-slot:item.createdAt="{ item }">{{
              item.createdAt | formatDate
            }}</template>
            <template v-slot:item.approvedAt="{ item }">{{
              item.approvedAt | formatDate
            }}</template>
            <template v-slot:item.supplierName="{ item }">
              <supplier-label
                v-if="item.supplier"
                v-model="item.supplierName"
                :supplier-type="item.supplier.serviceType"
                :supplier-id="item.supplier.id"
              ></supplier-label>
            </template>
            <template v-slot:item.supplierInvoiceDate="{ item }">{{
              item.supplierInvoiceDate | formatDate
            }}</template>
            <template v-slot:item.client.surname="{ item }">{{
              item.client ? item.client.surname : ""
            }}</template>
            <template v-slot:item.itineraryReference="{ item }">
              <reference-label
                v-model="item.itineraryReference"
                :itinerary-id="item.itinerary ? item.itinerary.id : ''"
                :new-tab="forPiApprovals"
              ></reference-label>
            </template>
            <template v-slot:item.agentName="{ item }">
              <agent-label
                v-if="item.agent"
                v-model="item.agentName"
                :agent-id="item.agent ? item.agent.id : ''"
              ></agent-label>
            </template>

            <template v-slot:item.exported="{ item }">
              <v-checkbox
                v-model="item.exported"
                primary
                hide-details
                :disabled="true"
                class="ma-0"
              ></v-checkbox>
            </template>
            <template v-slot:item.exportedAt="{ item }">{{
              item.exportedAt | formatDate
            }}</template>
            <template v-slot:item.approved="{ item }">
              <v-checkbox
                v-model="item.approved"
                primary
                hide-details
                :disabled="true"
                class="ma-0"
              ></v-checkbox>
            </template>
            <template v-slot:item.amount="{ item }">
              {{ item.amount | priceLabeler }}
            </template>
            <template v-slot:item.vatAmount="{ item }">
              {{ item.vatAmount | priceLabeler }}
            </template>
            <template v-slot:item.netAmount="{ item }">
              {{ item.netAmount | priceLabeler }}
            </template>
            <template v-slot:item.totalAdjustments="{ item }">
              {{ item.totalAdjustments | priceLabeler }}
            </template>
            <template v-slot:item.paid="{ item }">
              {{ item.paid | priceLabeler }}
            </template>
            <template v-slot:item.balance="{ item }">
              {{ item.balance | priceLabeler }}
            </template>
            <template v-slot:item.destinationName="{ item }">
              {{ item.supplier ? item.supplier.destinationName : "" }}
            </template>
            <template v-slot:item.payeeName="{ item }">
              {{ item.supplier ? item.supplier.payeeName : "" }}
            </template>
            <template v-slot:item.supplierEmail="{ item }">
              {{ item.supplier ? item.supplier.email : "" }}</template
            >
            <template v-slot:item.accountsEmail="{ item }">
              {{ item.supplier ? item.supplier.accountsEmail : "" }}
            </template>
            <template v-slot:item.paymentMethod="{ item }">
              {{ item.supplier ? item.supplier.paymentMethod : "" }}
            </template>
            <template v-slot:item.actions="{ item }">
              <default-list-actions
                :item="item"
                :hideView="true"
                :hideRemove="
                  item.payments.length > 0 ||
                  hideRemove ||
                  (item.number && item.number.indexOf('PC') > -1) ||
                  item.approved.toString() == 'true'
                "
                @deleteItem="deleteItem($event)"
                @viewItem="viewItem($event)"
                @editItem="editItem($event)"
              >
                <template v-slot:extraButton>
                  <v-tooltip bottom v-if="showApprove">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        small
                        class="mx-0"
                        @click="onApprove(item)"
                        :disabled="item && item.approved"
                      >
                        <v-icon color="blue" small>mdi-check</v-icon>
                      </v-btn>
                    </template>
                    <span>Approve Invoice</span>
                  </v-tooltip>
                </template>
              </default-list-actions>
            </template>
            <template v-slot:footer>
              <list-actions
                v-if="
                  selected.length > 0 &&
                  !$store.getters.hasAccessTo('finances:pi_view_only')
                "
                :selected="selected"
                list="purchaseInvoices"
                :columns="visibleColumns"
                :filter="listExportFilters"
                :hideExport="hideExport"
                @close="selected = []"
                @update="onInvoiceUpdate"
              >
                <v-btn fab dark small color="green" @click="onBatchPay">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-currency-usd</v-icon>
                    </template>
                    Batch Pay
                  </v-tooltip>
                </v-btn>

                <batch-attachment
                  @created="onBatchAttachmentCreate"
                  owner="PURCHASE_INVOICE"
                  :items="selected"
                ></batch-attachment>
                <v-btn fab dark small color="orange" @click="onBatchApprove">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-check</v-icon>
                    </template>
                    Batch Approve
                  </v-tooltip>
                </v-btn>
              </list-actions>
              <v-row justify="center" v-if="selected.length > 0">
                <h3 class="py-2 caption">
                  {{ selected.length }} record(s) selected
                </h3>
              </v-row>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="purchaseInvoices"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('purchaseInvoices')"
      ></get-more-results>
      <v-row
        ><list-totals v-model="totalsColumns" :items="items"></list-totals
      ></v-row>
      <batch-payments-dialog
        :show="showBatchPayment"
        @close="showBatchPayment = false"
        :invoices="selected"
        :columns="filteredHeaders"
      ></batch-payments-dialog>
    </v-flex>
    <v-flex v-else-if="forItinerary && items.length == 0" xs12>
      <v-row justify="center" align="center" class="pt-3">
        <v-btn
          v-if="!$store.getters.hasAccessTo('finances:pi_view_only')"
          fab
          dark
          large
          color="blue darken-1"
          @click="$emit('generate')"
        >
          <v-icon dark>mdi-receipt</v-icon>
        </v-btn>
      </v-row>
      <v-row justify="center" class="pt-1">
        <v-btn
          text
          color="primary"
          @click="$emit('generate')"
          :disabled="$store.getters.hasAccessTo('finances:pi_view_only')"
          >Create New Purchase Invoice</v-btn
        >
      </v-row>
    </v-flex>
    <basic-dialog
      :show="showDetailsDialog"
      @close="showDetailsDialog = false"
      max-width="85%"
    >
      <template v-if="selectedItem" v-slot:title>{{
        selectedItem.number
      }}</template>
      <purchase-invoice-details
        ref="details"
        v-model="selectedItem"
        @updated="onInvoiceUpdate"
      ></purchase-invoice-details>
      <template v-slot:actions>
        <v-btn
          text
          v-if="showXero"
          @click="onExportToXero"
          :disabled="
            $store.getters.hasAccessTo('finances:pi_view_only') ||
            exportInProgress
          "
          >Export to XERO</v-btn
        >
        <v-btn
          text
          v-if="showQuickBooks"
          @click="onExportToQuickBooks"
          :disabled="
            $store.getters.hasAccessTo('finances:pi_view_only') ||
            exportInProgress
          "
          >Export to Quick Books</v-btn
        >
        <v-btn
          @click="onSaveAndContinue"
          :disabled="$store.getters.hasAccessTo('finances:pi_view_only')"
          >Save and Continue</v-btn
        >
        <v-btn
          color="primary"
          @click="onSave"
          :disabled="$store.getters.hasAccessTo('finances:pi_view_only')"
          >Save and Close</v-btn
        >
      </template>
    </basic-dialog>
    <task-status
      :show="showBatchApproveTaskStatus"
      v-model="batchApproveTask"
      @complete="onBatchApproveComplete"
    ></task-status>
    <v-layout v-if="debug" wrap>
      {{ items }}
    </v-layout>
  </v-layout>
</template>
<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import {
  ALL_PURCHASE_INVOICES_SEARCH_QUERY,
  PURCHASE_INVOICE_DELETE_MUTATION,
  EXPORT_INVOICE_TO_XERO_MUTATION,
  EXPORT_INVOICE_TO_QUICK_BOOKS_MUTATION,
} from "../../../../constants/graphql";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import EventBus from "../../../../store/event-bus";
import ListFilters from "../../shared/list-filters/ListFilters";
import purchaseInvoicesFilters from "./purchase-invoices-filters";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import {
  removeGQLTypename,
  sortByDate,
  sortByDateWithFormat,
  sortByReference,
} from "../../../../helpers/index";
import ClientLabel from "../../shared/labels/ClientLabel";
import SupplierLabel from "../../shared/labels/SupplierLabel";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import AgentLabel from "../../shared/labels/AgentLabel";
import ListMenuActions from "../../shared/ListMenuActions";
import ListActions from "../../shared/list-actions/ListActions";
import BatchPaymentsDialog from "../purchase-invoice-payments/BatchPaymentsDialog";
import BatchApproveMixin from "./batch-approve-mixin";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../../shared/AgGridSwitch";
import BasicDialog from "../../shared/BasicDialog";
import PurchaseInvoiceDetails from "./PurchaseInvoiceDetails";
import TaskStatus from "../../shared/TaskStatus";
import ListTotals from "../../shared/ListTotals.vue";
import ListGrouped from "../../shared/ListGrouped.vue";
import PiTable from "./PiTable.vue";
import BatchAttachment from "../../shared/BatchAttachment.vue";

export default {
  name: "PurchaseInovoiceList",

  mixins: [ListMixin, BatchApproveMixin, AgGridListMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    ClientLabel,
    SupplierLabel,
    ReferenceLabel,
    AgentLabel,
    ListMenuActions,
    ListActions,
    BatchPaymentsDialog,
    AgGridVue,
    AgGridSwitch,
    BasicDialog,
    PurchaseInvoiceDetails,
    TaskStatus,
    ListTotals,
    ListGrouped,
    PiTable,
    BatchAttachment,
  },

  props: {
    customFilters: {
      type: Object,
    },
    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    hideTitle: {
      type: Boolean,
      default: () => false,
    },
    hideAgGrid: {
      type: Boolean,
      default: () => false,
    },
    multiSelect: {
      type: Boolean,
      default: () => true,
    },
    showApprove: {
      type: Boolean,
      default: () => false,
    },
    hideRemoveItem: {
      type: Boolean,
      default: () => false,
    },
    editItemCb: {
      type: Function,
      default: null,
    },
    showFilters: {
      type: Boolean,
      default: () => false,
    },
    skipLoad: {
      type: Boolean,
      default: () => false,
    },
    hideFilters: {
      type: Array,
      default: () => [],
    },
    forPiApprovals: {
      type: Boolean,
      default: () => false,
    },
    refName: {
      type: String,
      default: () => "purchaseInvoiceListFilters",
    },
    refNameColumns: {
      type: String,
      default: () => "PurchaseInovoiceList",
    },
    fixedHeader: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    return {
      debug: false,
      skip: this.skipLoad,
      selected: [],
      selectedItem: null,
      showGroupedView: false,
      selectedGroupedBy: {
        name: "Supplier",
        getKey: (item) => item.supplierName,
        getLabel: (item) => `Supplier: ${item.supplierName}`,
      },
      groupByOptions: [
        {
          name: "Supplier",
          getKey: (item) => item.supplierName,
          getLabel: (item) => `Supplier: ${item.supplierName}`,
        },
        {
          name: "Currency",
          getKey: (item) => item.currencyCode,
          getLabel: (item) => item.currencyCode,
        },
      ],
      showDetailsDialog: false,
      exportInProgress: false,
      purchaseInvoicesFilters: purchaseInvoicesFilters,
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "number",
        "supplierName",
        "itineraryReference",
        "amount",
        "currencyCode",
        "actions",
      ]),
      searchParams: {},
      headers: [
        {
          text: "",
          sortable: false,
          value: "menuActions",
          name: "menuActions",
        },
        {
          text: "Number",
          value: "number",
          name: "number",
        },
        {
          text: "Client",
          value: "clientName",
          sortable: false,
          name: "clientName",
          cellRendererFramework: "ClientLabel",
        },
        {
          text: "Supplier",
          value: "supplierName",
          name: "supplierName",
          sortable: true,
        },
        {
          text: "Destination",
          value: "destinationName",
          name: "destinationName",
          sortable: false,
          valueGetter: (payload) => payload.data.supplier.destinationName,
        },
        {
          text: "Payment Method",
          value: "paymentMethod",
          name: "paymentMethod",
          sortable: true,
          valueGetter: (payload) => payload.data.supplier.paymentMethod,
        },
        {
          text: "Payee Name",
          value: "payeeName",
          name: "payeeName",
          sortable: false,
          valueGetter: (payload) => payload.data.supplier.payeeName,
        },
        {
          text: "Supplier Email",
          value: "supplierEmail",
          name: "supplierEmail",
          sortable: false,
          valueGetter: (payload) => payload.data.supplier.email,
        },
        {
          text: "Accounts Email",
          value: "accountsEmail",
          name: "accountsEmail",
          sortable: false,
          valueGetter: (payload) => payload.data.supplier.accountsEmail,
        },
        {
          text: "Iti Ref",
          value: "itineraryReference",
          name: "itineraryReference",
          sortable: true,
          sort: sortByReference,
          comparator: sortByReference,
          cellRendererFramework: "ReferenceLabel",
        },
        {
          text: "Iti Client Ref",
          value: "itinerary.clientReference",
          name: "itineraryClientReference",
          sortable: true,
        },
        {
          text: "Iti Status",
          value: "itineraryStatus",
          name: "itineraryStatus",
          sortable: true,
        },
        {
          text: "Iti Internal Status",
          value: "itineraryInternalStatus",
          name: "itineraryInternalStatus",
          sortable: true,
        },
        {
          text: this.$store.getters.isCPS ? "Itinerary Destination" : "Iti Brand",
          value: "itineraryBrand",
          name: "itineraryBrand",
          sortable: true,
        },
        {
          text: "Vat Amount",
          value: "vatAmount",
          name: "vatAmount",
          sortable: true,
          groupBy: "currencyCode",
        },
        {
          text: "Net Amount",
          value: "netAmount",
          name: "netAmount",
          sortable: true,
          groupBy: "currencyCode",
        },
        {
          text: "Exchange Rate",
          value: "exchangeRate",
          name: "exchangeRate",
          sortable: true,
        },
        {
          text: "Iti Start Date",
          value: "itineraryStartDate",
          name: "itineraryStartDate",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.itineraryStartDate),
        },
        {
          text: "Service Start Date",
          value: "firstServiceStartDate",
          name: "firstServiceStartDate",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(
              payload.data.firstServiceStartDate
            ),
        },
        {
          text: "Amount",
          value: "amount",
          name: "amount",
          sortable: true,
          groupBy: "currencyCode",
        },
        {
          text: "Paid Amt",
          value: "paid",
          name: "paid",
          sortable: true,
          groupBy: "currencyCode",
        },
        {
          text: "Adjustments",
          value: "totalAdjustments",
          name: "totalAdjustments",
          sortable: true,
        },
        {
          text: "Balance",
          value: "balance",
          name: "balance",
          sortable: true,
          groupBy: "currencyCode",
        },
        {
          text: "Department",
          value: "department",
          name: "department",
          sortable: true,
        },
        {
          text: "Head Office",
          value: "headOfficeName",
          sortable: false,
          name: "headOfficeName",
        },
        {
          text: "Agent",
          value: "agentName",
          name: "agentName",
          cellRendererFramework: "AgentLabel",
          sortable: true,
        },
        {
          text: "Agent Account Code",
          value: "agentAccountCode",
          name: "agentAccountCode",
          sortable: false,
        },
        {
          text: "Currency",
          value: "currencyCode",
          name: "currencyCode",
          sortable: true,
        },
        {
          text: "Exported",
          value: "exported",
          name: "exported",
          sortable: true,
        },
        {
          text: "Exported At",
          value: "exportedAt",
          name: "exportedAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "Exported By",
          value: "exportedByName",
          name: "exportedByName",
          sortable: true,
        },
        {
          text: "Invoiced By",
          value: "invoicedByName",
          name: "invoicedByName",
          sortable: true,
        },

        {
          text: "Approved By",
          value: "approvedByName",
          sortable: false,
          name: "approvedByName",
        },

        {
          text: "Approved",
          value: "approved",
          name: "approved",
          sortable: true,
        },
        {
          text: "Approved At",
          value: "approvedAt",
          name: "approvedAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.approvedAt),
        },
        {
          text: "Created At",
          value: "createdAt",
          name: "createdAt",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "Supplier Inv No", //http://tracker.globetrack.ie/issues/5512
          value: "externalReference",
          name: "externalReference",
          sortable: true,
        },
        {
          text: "Supplier Inv Date", //http://tracker.globetrack.ie/issues/5512
          value: "supplierInvoiceDate",
          name: "supplierInvoiceDate",
          sortable: true,
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
        },
        {
          text: "Iti Internal Ref",
          value: "itineraryInternalRef",
          sortable: false,
          name: "itineraryInternalRef",
        },
        //notes
        {
          text: "Notes",
          value: "notes",
          sortable: false,
          name: "notes",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove:
                !this.$store.getters.hasAccessTo("finances:delete") ||
                this.$store.getters.hasAccessTo("finances:pi_view_only"),
              hideEdit: false,
            },
          }),
        },
      ],
      expandPanel: "",
      showBatchPayment: false,
    };
  },

  computed: {
    listExportFilters() {
      return {
        ...this.customFilters,
        ...this.searchParams,
      };
    },
    totalsColumns() {
      const cols = ["amount", "paid", "vatAmount", "netAmount", "balance"];
      return this.filteredHeaders.filter((h) => cols.indexOf(h.name) != -1);
    },
    hideExport() {
      return !this.$store.getters.hasAccessTo("finances:export");
    },

    showXero() {
      return this.$store.getters.hasAccessTo("finances:xero_export");
    },
    showQuickBooks() {
      return this.$store.getters.hasAccessTo("finances:quick_books");
    },

    filters() {
      return purchaseInvoicesFilters
        .filter((f) => this.hideFilters.indexOf(f.props.filterName) == -1)
        .map((f) => {
          f.props.outlined =
            this.$store.getters.localSettings("outlinedInputs");
          if (this.$store.getters.isCPS) {
            if (f.props.filterName == "brands_eq") {
              f.props.label = "Itinerary Destination";
            }
          }
          return f;
        });
    },
    items() {
      return this.purchaseInvoices && this.purchaseInvoices.edges
        ? this.purchaseInvoices.edges.map(removeGQLTypename)
        : [];
    },
    forItinerary() {
      return this.customFilters && this.customFilters.itinerary_id != "";
    },
    noFilters() {
      return Object.keys(this.searchParams).length < 1;
    },
    hideRemove() {
      return (
        this.hideRemoveItem ||
        !this.$store.getters.hasAccessTo("finances:delete") ||
        this.$store.getters.hasAccessTo("finances:pi_view_only")
      );
    },
  },

  watch: {
    customFilters(newFilters) {
      this.searchParams = { ...this.searchParams, ...newFilters };
    },
  },

  methods: {
    onBatchAttachmentCreate(payload) {
      console.log("onBatchAttachmentCreate", payload);
    },
    onUpdateSelection(payload) {
      //remove group from all selected items
      this.selected = this.selected.filter(
        (item) => !payload.items.includes(item)
      );
      //add new selected items
      this.selected = [...this.selected, ...payload.selected];
    },
    onApprove(payload) {
      this.$emit("approve", payload);
    },
    onSaveAndContinue() {
      this.$refs.details.savePurchaseInvoice(true);
    },
    onSave(s) {
      this.$refs.details.savePurchaseInvoice();
      this.showDetailsDialog = false;
    },
    onExportToXero() {
      this.exportInProgress = true;
      this.$apollo
        .mutate({
          mutation: EXPORT_INVOICE_TO_XERO_MUTATION,
          variables: {
            input: { invoiceId: this.selectedItem.id },
          },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportInvoiceToXero.message,
            color: data.exportInvoiceToXero.status,
          });
          this.exportInProgress = false;
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          this.exportInProgress = false;
        });
    },
    onExportToQuickBooks() {
      this.exportInProgress = true;
      this.$apollo
        .mutate({
          mutation: EXPORT_INVOICE_TO_QUICK_BOOKS_MUTATION,
          variables: { id: this.selectedItem.id },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportInvoiceToQuickBooks.message,
            color: data.exportInvoiceToQuickBooks.success ? "success" : "error",
          });
          this.exportInProgress = false;
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          this.exportInProgress = false;
        });
    },
    onBatchPay() {
      this.showBatchPayment = true;
    },
    onBatchApprove() {
      this.batchApprove(this.selected, "purchaseInvoices");
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: PURCHASE_INVOICE_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", {
        msg: "Purchase invoice removed.",
      });
      if (this.$apollo.queries.purchaseInvoices != null) {
        this.$apollo.queries.purchaseInvoices.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },
    searchParamsChanged(event) {
      this.skip = false;
      this.selected = [];
      this.searchParams = { ...this.customFilters, ...event };
    },
    showDetails(item) {
      this.selectedItem = { ...item };
      this.showDetailsDialog = true;
    },
    editItem(item) {
      if (this.editItemCb) {
        this.editItemCb(item);
      } else {
        //this.$router.push("/purchaseinvoice/" + item.id + "/details");
        this.selectedItem = { ...item };
        this.showDetailsDialog = true;
      }
    },
    reloadList() {
      if (this.$apollo.queries.purchaseInvoices != null) {
        this.$apollo.queries.purchaseInvoices.refetch();
        //this.selected = [];
      }
    },
    onInvoiceUpdate() {
      this.reloadList();
    },
  },
  created() {
    EventBus.$on("reloadPurchaseInvoiceList", () => {
      //console.log("reload list");
      if (this.$apollo.queries.purchaseInvoices != null) {
        this.$apollo.queries.purchaseInvoices.refetch();
        this.selected = [];
      }
    });

    this.expandPanel = this.context == "default_context" ? 0 : 1;
  },
  apollo: {
    purchaseInvoices: {
      query: ALL_PURCHASE_INVOICES_SEARCH_QUERY,
      variables() {
        // //fix for balance filter #7055
        // if (
        //   Object.keys(this.searchParams).some(
        //     (f) => f.indexOf("balance") != -1
        //   ) &&
        //   this.forPiApprovals
        // ) {
        //   console.log("removing balance not");
        //   delete this.customFilters.balance_not;
        //   delete this.searchParams.balance_not;
        //   console.log(this.customFilters, this.searchParams);
        // }
        return {
          after: this.after,
          first: this.first,
          filter: {
            ...this.customFilters,
            ...this.searchParams,
          },
          sort: this.sort,
        };
      },
      skip() {
        return this.skip;
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.purchaseInvoices
          ? data.purchaseInvoices.totalCount
          : 0;
      },
    },
  },
};
</script>
<style scoped>
#ag-pi-list {
  height: calc(100vh - 320px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/finances/purchase-invoices/PurchaseInvoiceList.vue